import { Activity, ArrowRight, DollarSign, Zap } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { AreaChart } from '@/components/charts/AreaChart'
import { BarChart } from '@/components/charts/BarChart'
import { StatCard } from '@/components/charts/StatCard'
import { AppShell } from '@/components/layout/AppShell'
import { useAuth } from '@/hooks/use-auth'
import { cn, formatNumber } from '@/lib/utils'
import { useMonitorStore } from '@/stores/monitor-store'

function formatMs(ms: number): string {
  if (ms < 1000) return `${ms}ms`
  return `${(ms / 1000).toFixed(1)}s`
}

function formatUsd(amount: number): string {
  if (amount < 0.01) return `$${amount.toFixed(4)}`
  if (amount < 1) return `$${amount.toFixed(3)}`
  return `$${amount.toFixed(2)}`
}

export function MonitorOverview() {
  useAuth()
  const { stats, isLoadingStats, costs, isLoadingCosts, fetchStats, fetchCosts } = useMonitorStore()
  const [costPeriod, setCostPeriod] = useState<'7d' | '30d' | 'all'>('30d')

  useEffect(() => {
    fetchStats()
    fetchCosts()
  }, [fetchStats, fetchCosts])

  const chartData = useMemo(() => {
    if (!stats?.requestsByDay) return []
    const byDate: Record<string, Record<string, string | number>> = {}
    for (const row of stats.requestsByDay) {
      if (!byDate[row.date]) byDate[row.date] = { date: row.date }
      const key = row.tag === 'person' ? 'compose' : row.tag === 'clothing' ? 'enhance' : 'other'
      byDate[row.date][key] = Number(byDate[row.date][key] ?? 0) + row.count
    }
    return Object.values(byDate)
  }, [stats])

  const creditsChartData = useMemo(() => {
    if (!stats?.creditsBreakdown) return []
    return Object.entries(stats.creditsBreakdown).map(([type, total]) => ({
      name: type.replace(/_/g, ' '),
      value: total,
    }))
  }, [stats])

  const successRate = useMemo(() => {
    if (!stats?.statusBreakdown) return 0
    const completed = stats.statusBreakdown.completed ?? 0
    const total = Object.values(stats.statusBreakdown).reduce((a, b) => a + b, 0)
    return total > 0 ? Math.round((completed / total) * 100) : 0
  }, [stats])

  const totalCreditsUsed = useMemo(() => {
    if (!stats?.creditsBreakdown) return 0
    return Object.values(stats.creditsBreakdown).reduce((a, b) => a + b, 0)
  }, [stats])

  return (
    <AppShell title="API Monitor">
      {isLoadingStats && !stats ? (
        <div className="flex items-center justify-center py-24">
          <p className="font-mono text-xs text-ink-faint animate-pulse">Loading monitor data...</p>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              label="Total Requests"
              value={stats?.totalRequests30d ?? 0}
              subtitle="Last 30 days"
            />
            <StatCard
              label="Success Rate"
              value={successRate}
              subtitle={`${formatNumber(stats?.statusBreakdown?.completed ?? 0)} completed`}
            />
            <StatCard
              label="Avg Generation"
              value={stats?.avgGenerationTimeMs ?? 0}
              subtitle={formatMs(stats?.avgGenerationTimeMs ?? 0)}
            />
            <StatCard label="Credits Consumed" value={totalCreditsUsed} subtitle="Last 30 days" />
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="rounded-lg border border-dashed border-border bg-surface p-5">
              <p className="mb-4 font-mono text-[11px] tracking-wider text-ink-faint uppercase">
                By Type
              </p>
              <div className="space-y-3">
                {Object.entries(stats?.typeBreakdown ?? {}).map(([tag, cnt]) => {
                  const label =
                    tag === 'person' ? 'Try-On (Compose)' : tag === 'clothing' ? 'Enhancement' : tag
                  const color = tag === 'person' ? 'bg-accent' : 'bg-positive'
                  const total = stats?.totalRequests30d ?? 1
                  const pct = Math.round((cnt / total) * 100)
                  return (
                    <div key={tag}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="font-mono text-xs text-ink">{label}</span>
                        <span className="font-mono text-[11px] tabular-nums text-ink-faint">
                          {formatNumber(cnt)} ({pct}%)
                        </span>
                      </div>
                      <div className="h-1.5 w-full rounded-full bg-border">
                        <div
                          className={cn('h-1.5 rounded-full', color)}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  )
                })}
                {Object.keys(stats?.typeBreakdown ?? {}).length === 0 && (
                  <p className="py-4 text-center font-mono text-xs text-ink-faint">No data</p>
                )}
              </div>
            </div>

            <div className="rounded-lg border border-dashed border-border bg-surface p-5">
              <p className="mb-4 font-mono text-[11px] tracking-wider text-ink-faint uppercase">
                By Status
              </p>
              <div className="space-y-3">
                {Object.entries(stats?.statusBreakdown ?? {}).map(([status, cnt]) => {
                  const colorMap: Record<string, string> = {
                    completed: 'bg-positive',
                    processing: 'bg-warning',
                    failed: 'bg-negative',
                  }
                  const total = stats?.totalRequests30d ?? 1
                  const pct = Math.round((cnt / total) * 100)
                  return (
                    <div key={status}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="font-mono text-xs text-ink capitalize">{status}</span>
                        <span className="font-mono text-[11px] tabular-nums text-ink-faint">
                          {formatNumber(cnt)} ({pct}%)
                        </span>
                      </div>
                      <div className="h-1.5 w-full rounded-full bg-border">
                        <div
                          className={cn('h-1.5 rounded-full', colorMap[status] ?? 'bg-ink-faint')}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  )
                })}
                {Object.keys(stats?.statusBreakdown ?? {}).length === 0 && (
                  <p className="py-4 text-center font-mono text-xs text-ink-faint">No data</p>
                )}
              </div>
            </div>

            <div className="rounded-lg border border-dashed border-border bg-surface p-5">
              <p className="mb-4 font-mono text-[11px] tracking-wider text-ink-faint uppercase">
                Credits by Action
              </p>
              <div className="space-y-3">
                {creditsChartData.map((item) => {
                  const colorMap: Record<string, string> = {
                    'fashion identify': 'bg-accent',
                    'tryon generation': 'bg-positive',
                    'photo enhancement': 'bg-warning',
                  }
                  const pct =
                    totalCreditsUsed > 0 ? Math.round((item.value / totalCreditsUsed) * 100) : 0
                  return (
                    <div key={item.name}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="font-mono text-xs text-ink capitalize">{item.name}</span>
                        <span className="font-mono text-[11px] tabular-nums text-ink-faint">
                          {formatNumber(item.value)} ({pct}%)
                        </span>
                      </div>
                      <div className="h-1.5 w-full rounded-full bg-border">
                        <div
                          className={cn(
                            'h-1.5 rounded-full',
                            colorMap[item.name] ?? 'bg-ink-faint'
                          )}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  )
                })}
                {creditsChartData.length === 0 && (
                  <p className="py-4 text-center font-mono text-xs text-ink-faint">No data</p>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <BarChart
              title="Requests Per Day"
              data={chartData}
              bars={[
                { dataKey: 'compose', color: '#C4714A', label: 'Try-On' },
                { dataKey: 'enhance', color: '#6B8F71', label: 'Enhancement' },
              ]}
            />

            <BarChart
              title="Credit Usage by Type"
              data={creditsChartData}
              bars={[{ dataKey: 'value', color: '#C4714A', label: 'Credits' }]}
              xKey="name"
            />
          </div>

          <div className="rounded-lg border border-dashed border-border bg-surface p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-accent" strokeWidth={1.5} />
                <p className="font-mono text-[11px] tracking-wider text-ink-faint uppercase">
                  Estimated API Costs
                </p>
              </div>
              <div className="flex rounded-md border border-dashed border-border bg-canvas">
                {(['7d', '30d', 'all'] as const).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setCostPeriod(p)}
                    className={cn(
                      'px-2.5 py-1.5 font-mono text-[10px] transition-colors first:rounded-l-md last:rounded-r-md',
                      costPeriod === p
                        ? 'bg-accent/10 text-accent'
                        : 'text-ink-muted hover:bg-surface-alt'
                    )}
                  >
                    {p === 'all' ? 'All time' : p === '7d' ? '7 days' : '30 days'}
                  </button>
                ))}
              </div>
            </div>

            {isLoadingCosts && !costs ? (
              <p className="py-6 text-center font-mono text-xs text-ink-faint animate-pulse">
                Calculating costs...
              </p>
            ) : costs?.periods[costPeriod] ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                  <div className="rounded-md border border-dashed border-border bg-canvas p-3">
                    <p className="font-mono text-[10px] text-ink-faint uppercase">
                      Total Est. Cost
                    </p>
                    <p className="mt-1 font-mono text-lg font-semibold tabular-nums text-ink">
                      {formatUsd(costs.periods[costPeriod].total)}
                    </p>
                  </div>
                  <div className="rounded-md border border-dashed border-border bg-canvas p-3">
                    <p className="font-mono text-[10px] text-ink-faint uppercase">
                      Try-On (Gemini)
                    </p>
                    <p className="mt-1 font-mono text-sm font-semibold tabular-nums text-ink">
                      {formatUsd(costs.periods[costPeriod].compose.cost)}
                    </p>
                    <p className="font-mono text-[10px] text-ink-faint">
                      {costs.periods[costPeriod].compose.count} requests
                    </p>
                  </div>
                  <div className="rounded-md border border-dashed border-border bg-canvas p-3">
                    <p className="font-mono text-[10px] text-ink-faint uppercase">
                      Enhance (Gemini)
                    </p>
                    <p className="mt-1 font-mono text-sm font-semibold tabular-nums text-ink">
                      {formatUsd(costs.periods[costPeriod].enhance.cost)}
                    </p>
                    <p className="font-mono text-[10px] text-ink-faint">
                      {costs.periods[costPeriod].enhance.count} requests
                    </p>
                  </div>
                  <div className="rounded-md border border-dashed border-border bg-canvas p-3">
                    <p className="font-mono text-[10px] text-ink-faint uppercase">
                      Fashion ID + Tags
                    </p>
                    <p className="mt-1 font-mono text-sm font-semibold tabular-nums text-ink">
                      {formatUsd(
                        costs.periods[costPeriod].fashionIdentify.cost +
                          costs.periods[costPeriod].groqTags.cost
                      )}
                    </p>
                    <p className="font-mono text-[10px] text-ink-faint">
                      {costs.periods[costPeriod].fashionIdentify.count} searches +{' '}
                      {costs.periods[costPeriod].groqTags.count} tags
                    </p>
                  </div>
                </div>

                <div className="rounded-md border border-dashed border-border bg-canvas p-3">
                  <p className="mb-2 font-mono text-[10px] text-ink-faint uppercase">
                    Unit Costs (per request)
                  </p>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    {costs.pricing &&
                      Object.entries(costs.pricing).map(([key, price]) => (
                        <div key={key} className="flex items-center justify-between">
                          <span className="font-mono text-[10px] text-ink-muted">
                            {key.replace(/_/g, ' ')}
                          </span>
                          <span className="font-mono text-[10px] tabular-nums text-ink">
                            {formatUsd(price)}
                          </span>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            ) : (
              <p className="py-6 text-center font-mono text-xs text-ink-faint">No cost data</p>
            )}
          </div>

          {costs?.costsByDay && costs.costsByDay.length > 0 && (
            <AreaChart
              title="Daily Estimated Cost (30d)"
              data={costs.costsByDay}
              dataKey="total"
              color="#C4714A"
            />
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Link
              to="/monitor/requests"
              className="group flex items-center justify-between rounded-lg border border-dashed border-border bg-surface p-5 transition-colors hover:bg-surface-alt"
            >
              <div className="flex items-center gap-3">
                <Activity className="h-5 w-5 text-accent" strokeWidth={1.5} />
                <div>
                  <p className="font-mono text-xs font-medium text-ink">Request Log</p>
                  <p className="font-mono text-[10px] text-ink-faint">
                    Browse all API requests with filters
                  </p>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-ink-faint transition-transform group-hover:translate-x-1" />
            </Link>

            <Link
              to="/monitor/storage"
              className="group flex items-center justify-between rounded-lg border border-dashed border-border bg-surface p-5 transition-colors hover:bg-surface-alt"
            >
              <div className="flex items-center gap-3">
                <Zap className="h-5 w-5 text-accent" strokeWidth={1.5} />
                <div>
                  <p className="font-mono text-xs font-medium text-ink">Storage Browser</p>
                  <p className="font-mono text-[10px] text-ink-faint">
                    Browse R2 files and user uploads
                  </p>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-ink-faint transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      )}
    </AppShell>
  )
}
